"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const redux_1 = require("redux");
const utils_3d_1 = require("~/components/Cad3d/utils/utils.3d");
const Viewer_1 = __importDefault(require("~/components/Cad3d/Viewer"));
const actions_1 = require("~/store/actions");
const consts_1 = require("~/store/consts");
const selectors_1 = require("~/store/selectors");
const types_1 = require("~/store/types");
const catalogUtils_1 = require("~/utils/catalogUtils");
function useItemReplacement() {
    var _a;
    // Hooks
    const extrusionsByExtrusionProfileType = (0, react_redux_1.useSelector)(selectors_1.selectExtrusionsByExtrusionProfileType);
    const panelReplacementMap = (0, react_redux_1.useSelector)(selectors_1.selectPanelReplacementMap);
    const tSlotCoverReplacementMap = (0, react_redux_1.useSelector)(selectors_1.selectTSlotCoverReplacementMap);
    // Action dispatchers
    const { dispachSetItemsReplacementDetails, dispatchToggleWidget } = (0, redux_1.bindActionCreators)({ dispachSetItemsReplacementDetails: actions_1.setItemsReplacementDetails, dispatchToggleWidget: actions_1.toggleWidget }, (0, react_redux_1.useDispatch)());
    // Data
    const selectedObjects = (_a = Viewer_1.default.instance.getDesignProMain()) === null || _a === void 0 ? void 0 : _a.selectionTool.selectedObjects;
    const isValidReplacement = (0, react_1.useCallback)((catalogItem) => catalogItem.published &&
        !catalogItem.deletedAt &&
        catalogItem.csvFileExists &&
        catalogItem.meshFileExists &&
        catalogItem.cadFileExists, []);
    const isValidReplacementOption = (0, react_1.useCallback)((replacementCandidate, possibleReplacementOption) => {
        const { item_number: candidateItemNumber } = replacementCandidate;
        const { item_number: optionItemNumber } = possibleReplacementOption;
        return optionItemNumber !== candidateItemNumber && isValidReplacement(possibleReplacementOption);
    }, [isValidReplacement]);
    const getSelectedItemNumberForReplacement = (0, react_1.useCallback)(() => {
        if (!(selectedObjects === null || selectedObjects === void 0 ? void 0 : selectedObjects.length)) {
            return null;
        }
        const distinctSelectedItemNumbers = [
            ...new Set(selectedObjects.map((obj) => obj.partDetails.itemNumber)),
        ];
        // All the selected items should have same item number for getting replaced
        if (distinctSelectedItemNumbers.length !== 1) {
            return null;
        }
        return distinctSelectedItemNumbers[0];
    }, [selectedObjects]);
    const getReplacementOptions = (0, react_1.useCallback)((getTypeSpecificReplacementOptionsFn) => {
        const selectedItemNumber = getSelectedItemNumberForReplacement();
        if (!selectedItemNumber) {
            return [];
        }
        return getTypeSpecificReplacementOptionsFn(selectedItemNumber);
    }, [getSelectedItemNumberForReplacement]);
    // Extrusions
    const getExtrusionProfileByItemNumberAndType = (0, react_1.useCallback)((itemNumber, profileType) => {
        var _a;
        return (_a = extrusionsByExtrusionProfileType[profileType]) === null || _a === void 0 ? void 0 : _a.find((extrusion) => extrusion.item_number === itemNumber);
    }, [extrusionsByExtrusionProfileType]);
    const areRequiredSlotsAvailableOnExtrusion = (0, react_1.useCallback)((extrusion, requiredSlots) => {
        var _a;
        if (!(requiredSlots === null || requiredSlots === void 0 ? void 0 : requiredSlots.length)) {
            return true;
        }
        const availableSlots = (_a = extrusion.extrusionType) === null || _a === void 0 ? void 0 : _a.slotNumbers;
        if (!availableSlots) {
            return false;
        }
        let lastFoundSlotIndexInExtrusionSlots = -1;
        return requiredSlots.every((slot) => {
            const requiredSlotIndexInExtrusion = availableSlots.indexOf(slot);
            const isOrderValid = requiredSlotIndexInExtrusion > lastFoundSlotIndexInExtrusionSlots;
            lastFoundSlotIndexInExtrusionSlots = requiredSlotIndexInExtrusion;
            return isOrderValid;
        });
    }, []);
    // A valid extrusion profile replacement option will have
    //  1. same extrusion profile type
    //  2. different item number
    //  3. same side lengths
    //  4. same convex state
    //  5. slot numbers as ordered-superset of the profile in question
    // along with other validation criteria satisfied.
    const isValidExtrusionReplacementOption = (0, react_1.useCallback)((replacementCandidate, possibleReplacementOption) => {
        const { item_number: candidateItemNumber, extrusionType: candidateExtrusionType } = replacementCandidate;
        const { item_number: optionItemNumber, extrusionType: optionExtrusionType } = possibleReplacementOption;
        return (isValidReplacementOption(replacementCandidate, possibleReplacementOption) &&
            candidateExtrusionType &&
            (optionExtrusionType === null || optionExtrusionType === void 0 ? void 0 : optionExtrusionType.extrusionProfileTypeId) === candidateExtrusionType.extrusionProfileTypeId &&
            optionItemNumber !== candidateItemNumber &&
            optionExtrusionType.extrusionProfileType.seriesTypeId ===
                candidateExtrusionType.extrusionProfileType.seriesTypeId &&
            optionExtrusionType.sideA &&
            candidateExtrusionType.sideA &&
            (0, utils_3d_1.areEqual)(optionExtrusionType.sideA, candidateExtrusionType.sideA) &&
            optionExtrusionType.sideB &&
            candidateExtrusionType.sideB &&
            (0, utils_3d_1.areEqual)(optionExtrusionType.sideB, candidateExtrusionType.sideB) &&
            (0, catalogUtils_1.isConvexItem)(optionItemNumber) === (0, catalogUtils_1.isConvexItem)(candidateItemNumber) &&
            areRequiredSlotsAvailableOnExtrusion(possibleReplacementOption, candidateExtrusionType.slotNumbers));
    }, [areRequiredSlotsAvailableOnExtrusion, isValidReplacementOption]);
    const getExtrusionReplacementOptions = (0, react_1.useCallback)((selectedExtrusionItemNumber, selectedExtrusionProfileType) => {
        var _a;
        const selectedExtrusionProfile = getExtrusionProfileByItemNumberAndType(selectedExtrusionItemNumber, selectedExtrusionProfileType);
        if (!selectedExtrusionProfile) {
            return [];
        }
        return ((_a = extrusionsByExtrusionProfileType[selectedExtrusionProfileType]) !== null && _a !== void 0 ? _a : []).filter((extrusion) => isValidExtrusionReplacementOption(selectedExtrusionProfile, extrusion));
    }, [extrusionsByExtrusionProfileType, getExtrusionProfileByItemNumberAndType, isValidExtrusionReplacementOption]);
    const shouldShowReplaceOptionForExtrusions = (0, react_1.useCallback)(() => {
        var _a;
        if (!(selectedObjects === null || selectedObjects === void 0 ? void 0 : selectedObjects.length)) {
            return false;
        }
        const firstObject = selectedObjects[0];
        if (!((_a = firstObject === null || firstObject === void 0 ? void 0 : firstObject.partDetails) === null || _a === void 0 ? void 0 : _a.extrusionProfileType)) {
            return false;
        }
        const shouldShow = getReplacementOptions((selectedExtrusionItemNumber) => {
            return getExtrusionReplacementOptions(selectedExtrusionItemNumber, firstObject.partDetails.extrusionProfileType);
        }).length > 0;
        return shouldShow;
    }, [getExtrusionReplacementOptions, getReplacementOptions, selectedObjects]);
    // Panels
    const getPanelFromPanelReplacementMap = (0, react_1.useCallback)((panelItemNumber, seriesLookupKey, thicknessLookupKey) => {
        var _a, _b;
        return (_b = (_a = panelReplacementMap[seriesLookupKey]) === null || _a === void 0 ? void 0 : _a[thicknessLookupKey]) === null || _b === void 0 ? void 0 : _b.find((panel) => panel.item_number === panelItemNumber);
    }, [panelReplacementMap]);
    const getPanelReplacementOptions = (0, react_1.useCallback)((selectedPanelItemNumber, seriesIds, thickness) => {
        const seriesLookupKey = (0, catalogUtils_1.generateReplacementMapSeriesKey)(seriesIds);
        const thicknessLookupKey = (0, catalogUtils_1.generatePanelReplacementMapThicknessKey)(thickness);
        const selectedPanel = getPanelFromPanelReplacementMap(selectedPanelItemNumber, seriesLookupKey, thicknessLookupKey);
        if (!selectedPanel) {
            return [];
        }
        return panelReplacementMap[seriesLookupKey][thicknessLookupKey].filter((panel) => isValidReplacementOption(selectedPanel, panel));
    }, [getPanelFromPanelReplacementMap, isValidReplacementOption, panelReplacementMap]);
    const shouldShowReplaceOptionForPanels = (0, react_1.useCallback)(() => {
        if (!(selectedObjects === null || selectedObjects === void 0 ? void 0 : selectedObjects.length)) {
            return false;
        }
        const firstObject = selectedObjects[0];
        if (!(firstObject === null || firstObject === void 0 ? void 0 : firstObject.partDetails)) {
            return false;
        }
        const shouldShow = getReplacementOptions((selectedPanelItemNumber) => {
            const { seriesIds, thickness } = firstObject.partDetails;
            return getPanelReplacementOptions(selectedPanelItemNumber, seriesIds, thickness);
        });
        return shouldShow.length > 0;
    }, [getPanelReplacementOptions, getReplacementOptions, selectedObjects]);
    // T-Slot Covers
    const getTSlotCoverFromReplacementMap = (0, react_1.useCallback)((tSlotCoverItemNumber, seriesLookupKey) => {
        var _a;
        return (_a = tSlotCoverReplacementMap[seriesLookupKey]) === null || _a === void 0 ? void 0 : _a.find((tSlotCover) => tSlotCover.item_number === tSlotCoverItemNumber);
    }, [tSlotCoverReplacementMap]);
    const getTSlotCoverReplacementOptions = (0, react_1.useCallback)((selectedTSlotCoverItemNumber, seriesIds) => {
        var _a;
        const seriesLookupKey = (0, catalogUtils_1.generateReplacementMapSeriesKey)(seriesIds);
        const selectedTSlotCover = getTSlotCoverFromReplacementMap(selectedTSlotCoverItemNumber, seriesLookupKey);
        if (!selectedTSlotCover) {
            return [];
        }
        return ((_a = tSlotCoverReplacementMap[seriesLookupKey]) !== null && _a !== void 0 ? _a : []).filter((tSlotCover) => isValidReplacementOption(selectedTSlotCover, tSlotCover));
    }, [getTSlotCoverFromReplacementMap, isValidReplacementOption, tSlotCoverReplacementMap]);
    const shouldShowReplaceOptionForTSlotCovers = (0, react_1.useCallback)(() => {
        if (!(selectedObjects === null || selectedObjects === void 0 ? void 0 : selectedObjects.length)) {
            return false;
        }
        const firstObject = selectedObjects[0];
        if (!(firstObject === null || firstObject === void 0 ? void 0 : firstObject.partDetails)) {
            return false;
        }
        const shouldShow = getReplacementOptions((selectedTSlotCoverItemNumber) => {
            const { seriesIds } = firstObject.partDetails;
            return getTSlotCoverReplacementOptions(selectedTSlotCoverItemNumber, seriesIds);
        });
        return shouldShow.length > 0;
    }, [getReplacementOptions, getTSlotCoverReplacementOptions, selectedObjects]);
    const shouldShowReplaceMenu = (0, react_1.useCallback)(() => {
        var _a, _b;
        // Check if only one type of items are selected
        const selectedTypes = Array.from((_b = (_a = Viewer_1.default.instance.selectedObjectsByType) === null || _a === void 0 ? void 0 : _a.keys()) !== null && _b !== void 0 ? _b : []);
        if ((selectedTypes === null || selectedTypes === void 0 ? void 0 : selectedTypes.length) !== 1) {
            return false;
        }
        const selectedType = selectedTypes[0];
        switch (selectedType) {
            case types_1.ITEM_TYPE.EXTRUSION:
                return shouldShowReplaceOptionForExtrusions();
            case types_1.COMPONENT_TYPE.PANEL:
                return shouldShowReplaceOptionForPanels();
            case types_1.COMPONENT_TYPE.TSLOT_COVER:
                return shouldShowReplaceOptionForTSlotCovers();
            default:
                return false;
        }
    }, [shouldShowReplaceOptionForExtrusions, shouldShowReplaceOptionForPanels, shouldShowReplaceOptionForTSlotCovers]);
    const prepareItemReplacementState = (0, react_1.useCallback)((typeOfItemsToReplace) => {
        const anySelectedObject = selectedObjects[0];
        if (!(anySelectedObject === null || anySelectedObject === void 0 ? void 0 : anySelectedObject.partDetails)) {
            return null;
        }
        const { partDetails: selectedObjectPartDetails } = anySelectedObject;
        const { itemNumber: selectedObjectItemNumber } = selectedObjectPartDetails;
        let itemToBeReplaced;
        let availableReplacements;
        switch (typeOfItemsToReplace) {
            case types_1.ITEM_TYPE.EXTRUSION: {
                const { extrusionProfileType: selectedObjectExtrusionProfileType } = selectedObjectPartDetails;
                itemToBeReplaced = getExtrusionProfileByItemNumberAndType(selectedObjectItemNumber, selectedObjectExtrusionProfileType);
                availableReplacements = getExtrusionReplacementOptions(selectedObjectItemNumber, selectedObjectExtrusionProfileType);
                break;
            }
            case types_1.COMPONENT_TYPE.PANEL: {
                const { seriesIds, thickness } = selectedObjectPartDetails;
                const seriesLookupKey = (0, catalogUtils_1.generateReplacementMapSeriesKey)(seriesIds);
                const thicknessLookupKey = (0, catalogUtils_1.generatePanelReplacementMapThicknessKey)(thickness);
                itemToBeReplaced = getPanelFromPanelReplacementMap(selectedObjectItemNumber, seriesLookupKey, thicknessLookupKey);
                availableReplacements = getPanelReplacementOptions(selectedObjectItemNumber, seriesIds, thickness);
                break;
            }
            case types_1.COMPONENT_TYPE.TSLOT_COVER: {
                const { seriesIds } = selectedObjectPartDetails;
                const seriesLookupKey = (0, catalogUtils_1.generateReplacementMapSeriesKey)(seriesIds);
                itemToBeReplaced = getTSlotCoverFromReplacementMap(selectedObjectItemNumber, seriesLookupKey);
                availableReplacements = getTSlotCoverReplacementOptions(selectedObjectItemNumber, seriesIds);
                break;
            }
            default:
                break;
        }
        if (!itemToBeReplaced || !(availableReplacements === null || availableReplacements === void 0 ? void 0 : availableReplacements.length)) {
            return null;
        }
        return {
            typeOfItemsToReplace,
            itemToBeReplaced,
            availableReplacements,
        };
    }, [
        getExtrusionProfileByItemNumberAndType,
        getExtrusionReplacementOptions,
        getPanelFromPanelReplacementMap,
        getPanelReplacementOptions,
        getTSlotCoverFromReplacementMap,
        getTSlotCoverReplacementOptions,
        selectedObjects,
    ]);
    // Handlers
    const openItemReplacementWidget = (0, react_1.useCallback)(() => {
        const { selectedObjectsByType } = Viewer_1.default.instance;
        if ((selectedObjects === null || selectedObjects === void 0 ? void 0 : selectedObjects.length) > 0 && (selectedObjectsByType === null || selectedObjectsByType === void 0 ? void 0 : selectedObjectsByType.keys())) {
            const typeOfItemsToReplace = Array.from(selectedObjectsByType.keys())[0];
            const itemReplacementState = prepareItemReplacementState(typeOfItemsToReplace);
            if (itemReplacementState) {
                dispachSetItemsReplacementDetails(itemReplacementState);
                dispatchToggleWidget({
                    widgetName: consts_1.WIDGETS.ITEM_REPLACEMENT_WIDGET,
                    show: true,
                });
            }
        }
    }, [dispachSetItemsReplacementDetails, dispatchToggleWidget, prepareItemReplacementState, selectedObjects === null || selectedObjects === void 0 ? void 0 : selectedObjects.length]);
    return { shouldShowReplaceMenu, openItemReplacementWidget };
}
exports.default = useItemReplacement;
